import React, { useEffect, useState } from 'react';
import apiClient from '../../Api/apiConfig';
import {
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  useReactTable,
  getSortedRowModel,
} from '@tanstack/react-table';
import { Edit, Trash2, Building } from 'lucide-react';
import "../../styles/LandlordModal.css";

const columnHelper = createColumnHelper();

export default function LandlordsTable() {
  const [data, setData] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  useEffect(() => {
    const fetchLandlords = async () => {
      try {
        const response = await apiClient.get('/landlords');
        setData(response.data);
        setIsLoading(false);
      } catch (err) {
        console.error('Erreur lors du chargement des bailleurs :', err);
        setError('Impossible de charger les bailleurs. Veuillez réessayer.');
        setIsLoading(false);
      }
    };

    fetchLandlords();
  }, []);

  const columns = [
    columnHelper.accessor('name', {
      header: 'Nom',
      cell: (info) => info.getValue(),
    }),
    columnHelper.accessor('email', {
      header: 'Email',
      cell: (info) => info.getValue(),
    }),
    columnHelper.accessor('phone', {
      header: 'Téléphone',
      cell: (info) => info.getValue(),
    }),
    columnHelper.accessor('address', {
      header: 'Adresse',
      cell: (info) => info.getValue(),
    }),
    columnHelper.accessor('id', {
      header: 'Actions',
      cell: (info) => (
        <div className="flex space-x-2">
          <button
            onClick={() => console.log('Edit landlord:', info.row.original)}
            className="p-1 text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300"
          >
            <Edit className="h-5 w-5" />
          </button>
          <button
            onClick={() => console.log('Delete landlord:', info.row.original)}
            className="p-1 text-red-600 hover:text-red-800 dark:text-red-400 dark:hover:text-red-300"
          >
            <Trash2 className="h-5 w-5" />
          </button>
        </div>
      ),
    }),
  ];

  const table = useReactTable({
    data,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
  });

  if (isLoading) {
    return (
      <div className="text-center mt-8">
        <p className="text-gray-600">Chargement des bailleurs...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center mt-8">
        <p className="text-red-600">{error}</p>
      </div>
    );
  }

  return (
    <div className="mt-8 flex flex-col bg-white dark:bg-gray-800 shadow rounded-lg">
      <div className="p-4 border-b border-gray-200 dark:border-gray-700 flex justify-between items-center">
        <h2 className="text-lg font-medium text-gray-900 dark:text-white">Gestion des bailleurs</h2>
        <button
          onClick={() => setIsModalOpen(true)}
          className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
        >
          <Building className="h-5 w-5 mr-2" />
          Ajouter un bailleur
        </button>
      </div>
      <div className="-mx-4 -my-2 overflow-x-auto sm:-mx-6 lg:-mx-8">
        <div className="inline-block min-w-full py-2 align-middle md:px-6 lg:px-8">
          <div className="overflow-hidden shadow ring-1 ring-black ring-opacity-5 md:rounded-lg">
            <table className="min-w-full divide-y divide-gray-300">
              <thead className="bg-gray-50">
                {table.getHeaderGroups().map((headerGroup) => (
                  <tr key={headerGroup.id}>
                    {headerGroup.headers.map((header) => (
                      <th
                        key={header.id}
                        scope="col"
                        className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900"
                      >
                        {header.isPlaceholder
                          ? null
                          : flexRender(header.column.columnDef.header, header.getContext())}
                      </th>
                    ))}
                  </tr>
                ))}
              </thead>
              <tbody className="divide-y divide-gray-200 bg-white dark:bg-gray-800">
                {table.getRowModel().rows.map((row) => (
                  <tr key={row.id}>
                    {row.getVisibleCells().map((cell) => (
                      <td
                        key={cell.id}
                        className="whitespace-nowrap px-3 py-4 text-sm text-gray-500"
                      >
                        {flexRender(cell.column.columnDef.cell, cell.getContext())}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Modal Popup */}
      {isModalOpen && (
        <div className="modal-overlay">
          <div className="modal-container">
            <h3 className="text-lg font-medium text-white">Ajouter un bailleur</h3>
            <form>
              <div className="form-group">
                <label className="block text-white">Nom</label>
                <input type="text" className="mt-1 p-2 w-full border rounded-md" />
              </div>
              <div className="form-group">
                <label className="block text-white">Email</label>
                <input type="email" className="mt-1 p-2 w-full border rounded-md" />
              </div>
              <div className="form-group">
                <label className="block text-white">Téléphone</label>
                <input type="tel" className="mt-1 p-2 w-full border rounded-md" />
              </div>
              <div className="form-group">
                <label className="block text-white">Adresse</label>
                <input type="text" className="mt-1 p-2 w-full border rounded-md" />
              </div>
              <div className="modal-actions">
                <button
                  type="button"
                  onClick={() => setIsModalOpen(false)}
                  className="cancel-button"
                >
                  Annuler
                </button>
                <button
                  type="submit"
                  className="submit-button"
                >
                  Ajouter
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
